import { dynamicElementsDropdown } from '@epicuro-next/constants/dynamic-elements';
import {
  CONFIGURATIONS_PROVIDER_KEY,
  OPENED_LIST_DATA_PROVIDER_KEY,
} from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicItem,
  LogicOperator,
  Operation,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  ProductListConfigurationKeys,
  PRODUCT_LIST_NAMESPACE,
} from '../../../product-list-rules';
import {
  isListAllowedForEdit,
  isListNotDeleted,
  isNormalList,
  isNotCommercialList,
  isNotOwnerOfOpenedList,
  isOwnerOfOpenedList,
} from '../if-statements/if-statements';

export const groupByDropdownItemIfActiveConfig = {
  none: [
    LogicOperator.EQ,
    [
      {
        provider: OPENED_LIST_DATA_PROVIDER_KEY,
        property: 'sortType',
      },
      null,
    ],
  ],
  category: [
    LogicOperator.EQ,
    [
      {
        provider: OPENED_LIST_DATA_PROVIDER_KEY,
        property: 'sortType',
      },
      'Category',
    ],
  ],
};

const groupOptions = [
  {
    type: 'label',
    config: { text: '@@listOptionsGroupingHeader' },
    if: isNormalList,
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsGroupingNone',
      action: {
        type: 'change-products-grouping-list',
        value: null,
      },
      active: groupByDropdownItemIfActiveConfig.none,
    },
    if: isNormalList,
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsGroupingCategory',
      action: {
        type: 'change-products-grouping-list',
        value: 'Category',
      },
      active: groupByDropdownItemIfActiveConfig.category,
    },
    if: isNormalList,
  },
];

const actionItems = [
  {
    type: 'item',
    config: {
      text: '@@listOptionsSaveList',
      icon: ['fas', 'copy'],
      action: 'clone-list',
    },
    if: [LogicOperator.AND, [isNotOwnerOfOpenedList, isNotCommercialList]],
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsEditList',
      icon: ['fas', 'pencil'],
      action: 'edit-list',
    },
    if: isListAllowedForEdit,
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsDownloadPDF',
      icon: ['fas', 'file-pdf'],
      action: 'download-pdf-list',
    },
    if: isOwnerOfOpenedList,
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsDownloadData',
      icon: ['fas', 'file-excel'],
      action: 'download-data-list',
    },
    if: [
      LogicOperator.AND,
      [
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName:
                  ProductListConfigurationKeys.ShowDownloadDataButton,
                defaultValue: false,
              },
            },
            true,
          ],
        ],
        isOwnerOfOpenedList,
        isNotCommercialList,
      ],
    ],
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsDownloadListImages',
      icon: ['fas', 'folder-arrow-down'],
      action: 'download-images-list',
    },
    if: [
      LogicOperator.AND,
      [
        isOwnerOfOpenedList,
        isNotCommercialList,
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName:
                  ProductListConfigurationKeys.ShowDownloadImagesButton,
                defaultValue: false,
              },
            },
            true,
          ],
        ],
      ],
    ],
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsShowBulkadd',
      icon: ['fas', 'grid-2-plus'],
      action: 'bulk-add-products-list',
    },
    if: [
      LogicOperator.AND,
      [
        isOwnerOfOpenedList,
        isNotCommercialList,
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName:
                  ProductListConfigurationKeys.ShowProductsAutocomplete,
                defaultValue: true,
              },
            },
            false,
          ],
        ],
      ],
    ],
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsCloneList',
      icon: ['fas', 'copy'],
      action: 'clone-list',
    },
    if: [LogicOperator.AND, [isOwnerOfOpenedList, isNotCommercialList]],
  },
  {
    type: 'item',
    config: {
      text: '@@listOptionsDeleteList',
      icon: ['fas', 'trash-list'],
      action: 'delete-list',
    },
    if: isOwnerOfOpenedList,
  },
];

const actionsDropdownDefaultConfig: IDynamicItem = {
  contentTypeId: dynamicElementsDropdown,
  styles: {
    variables: {
      '--btn-dropdown-trigger-padding': '0',
      '--btn-dropdown-trigger-width': '2.25rem',
      '--btn-dropdown-trigger-height': '2.25rem',
      '--btn-dropdown-trigger-display': 'flex',
      '--btn-dropdown-trigger-justify-content': 'center',
      '--btn-dropdown-trigger-align-items': 'center',
    },
  },
  inputs: {
    icon: ['fas', 'gear'],
    dropdownItems: [],
  },
};

export function getActionsDropdownDefaultConfig(
  isGroupingEnabled = true,
  ifOperation?: Operation,
): IDynamicItem {
  const dropdownItems = isGroupingEnabled
    ? [...actionItems, ...groupOptions]
    : actionItems;

  return {
    ...actionsDropdownDefaultConfig,
    if: ifOperation
      ? [LogicOperator.AND, [isListNotDeleted, ifOperation]]
      : isListNotDeleted,
    inputs: {
      ...actionsDropdownDefaultConfig.inputs,
      dropdownItems,
    },
  };
}
